import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const STATUS_LABELS = {
  cust_acceptance: 'Customer Acceptance',
  design: 'Design',
  design_approval: 'Design Approval',
  final_quotation: 'Final Quotation',
  quotation_approval: 'Quotation Approval',
  documents: 'Documents',
  project_started: 'Project Started',
  project: 'Project',
  customer_acceptance: 'Customer Acceptance',
  completed: 'Completed',
  drop: 'Drop',
  cancelled: 'Cancelled',
};

const STATUS_COLORS = {
  cust_acceptance: 'bg-amber-50 text-amber-700 border-amber-200',
  customer_acceptance: 'bg-amber-50 text-amber-700 border-amber-200',
  design: 'bg-blue-50 text-blue-700 border-blue-200',
  design_approval: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  final_quotation: 'bg-purple-50 text-purple-700 border-purple-200',
  quotation_approval: 'bg-violet-50 text-violet-700 border-violet-200',
  project_started: 'bg-green-50 text-green-700 border-green-200',
  project: 'bg-teal-50 text-teal-700 border-teal-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  drop: 'bg-rose-50 text-rose-700 border-rose-200',
  cancelled: 'bg-slate-50 text-slate-500 border-slate-200',
};

export const DashboardProjectSearchTable = ({ projects = [], search = '' }) => {
  const navigate = useNavigate();

  if (!search.trim()) return null;

  return (
    <div className="card overflow-hidden border border-slate-200 rounded-xl shadow-sm bg-white animate-in fade-in slide-in-from-top-2 duration-200">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50/60">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-slate-800">SD Projects</h3>
          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-medium">
            {projects.length} result{projects.length !== 1 ? 's' : ''}
          </span>
          <span className="text-xs text-slate-400 italic">Searching by Space Designs ID: "{search}"</span>
        </div>
        <button
          onClick={() => navigate('/projects')}
          className="flex items-center gap-1 text-xs text-brand-blue font-medium hover:underline"
        >
          View all projects <ArrowUpRight size={13} />
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-slate-400">
          <span className="text-3xl mb-2">🔍</span>
          <p className="text-sm font-medium">No SD projects match "<span className="font-semibold text-slate-600">{search}</span>"</p>
          <p className="text-xs mt-1">Try a different Space Designs ID</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left text-[13px] font-semibold text-slate-500 uppercase tracking-wide px-4 py-3">Space Designs ID</th>
                <th className="text-left text-[13px] font-semibold text-slate-500 uppercase tracking-wide px-4 py-3">Customer</th>
                <th className="text-left text-[13px] font-semibold text-slate-500 uppercase tracking-wide px-4 py-3">Phone</th>
                <th className="text-left text-[13px] font-semibold text-slate-500 uppercase tracking-wide px-4 py-3">Location</th>
                <th className="text-left text-[13px] font-semibold text-slate-500 uppercase tracking-wide px-4 py-3">Lead Status</th>
                <th className="text-left text-[13px] font-semibold text-slate-500 uppercase tracking-wide px-4 py-3">Last Note</th>
                <th className="text-left text-[13px] font-semibold text-slate-500 uppercase tracking-wide px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {projects.map((project, idx) => {
                const name = project.customerName ||
                  `${project.firstName || ''} ${project.lastName || ''}`.trim() ||
                  'No Name';
                const sdId = project.customId || project.projectId || project.projectNumber || '—';
                const status = project.status || project.leadStatus || project.projectStatus;
                const statusLabel = STATUS_LABELS[status] || status || '—';
                const statusColor = STATUS_COLORS[status] || 'bg-slate-50 text-slate-500 border-slate-200';
                let lastNote = project.lastNote || project.notes || '—';
                if (lastNote && typeof lastNote === 'string' && lastNote.includes(' | Note: ')) {
                  lastNote = lastNote.split(' | Note: ')[1].trim();
                }
                const createdAt = project.createdAt
                  ? new Date(project.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
                  : '—';

                return (
                  <tr
                    key={project._id || idx}
                    className="hover:bg-slate-50/70 transition-colors cursor-pointer group"
                    onClick={() => navigate('/projects')}
                  >
                    <td className="px-4 py-3">
                      <span className="text-[11px] font-semibold uppercase tracking-tight text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                        {sdId}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-[10px] font-bold">{name.charAt(0).toUpperCase()}</span>
                        </div>
                        <span className="text-[12px] font-medium text-slate-800 truncate max-w-[130px]">{name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-slate-500">
                      {project.phoneNumber || project.phone || '—'}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-slate-600 capitalize">
                      {project.city || (project.location ? project.location.split(',')[0] : '—')}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${statusColor}`}>
                        {statusLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-[200px]">
                      <span className="text-[11px] text-slate-500 italic truncate block">{lastNote}</span>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-slate-400 whitespace-nowrap">
                      {createdAt}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};





